import axios from 'axios';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import API_URL from '../config/api';
import './Dashboard.css';

const SurveyProfilerCategoryQuestions = () => {
  const navigate = useNavigate();
  const { category, subcategory } = useParams();
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentAnswer, setCurrentAnswer] = useState([]);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [categoryInfo, setCategoryInfo] = useState(null);
  const [stats, setStats] = useState({ answered: 0, total: 0 });

  // Category structure for display
  const categoryStructure = {
    personal_information: {
      title: 'Personal Information',
      icon: '👤'
    },
    family_information: {
      title: 'Family Information',
      icon: '👨‍👩‍👧‍👦'
    },
    employment_situation: {
      title: 'Employment Situation',
      icon: '💼'
    },
    financial_survey: {
      title: 'Financial Survey',
      icon: '💰'
    },
    health_survey: {
      title: 'Health Survey',
      icon: '🏥'
    },
    shopping_habits: {
      title: 'Shopping Habits',
      icon: '🛒'
    },
    recreation_entertainment: {
      title: 'Recreation and Entertainment',
      icon: '🎭'
    },
    lifestyle: {
      title: 'Lifestyle',
      icon: '🎯'
    },
    travel_survey: {
      title: 'Travel Survey',
      icon: '✈️'
    }
  };

  const subcategoryTitles = {
    basic_info: 'Basic Information',
    education: 'Education',
    location: 'Location',
    employment_status: 'Employment Status',
    occupation: 'Occupation',
    industry: 'Industry',
    income: 'Income',
    investments: 'Investments',
    expenses: 'Expenses',
    retail: 'Retail Shopping',
    online: 'Online Shopping',
    brands: 'Brand Preferences',
    hobbies: 'Hobbies',
    sports: 'Sports & Fitness',
    media: 'Media & Entertainment'
  };

  useEffect(() => {
    const cat = categoryStructure[category];
    if (cat) {
      setCategoryInfo({
        ...cat,
        subcategory: subcategoryTitles[subcategory] || subcategory
      });
    }
    fetchCategoryQuestions();
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, subcategory]);

  useEffect(() => {
    // Load previous answer - handle corrupted checkbox data
    if (questions.length > 0 && currentQuestionIndex < questions.length) {
      const currentQuestion = questions[currentQuestionIndex];
      let answer = currentQuestion.userAnswer;

      if (currentQuestion.questionType === 'checkbox') {
        if (!answer || !Array.isArray(answer)) {
          answer = [];
        } else if (answer.length > 1) {
          const isNoneOption = (optionValue) => {
            const opt = currentQuestion.options?.find(o => (o.value || o.label) === optionValue);
            if (!opt) return false;
            const label = (opt.label || opt.value).toLowerCase().trim();
            return label.includes('none');
          };

          const hasNone = answer.some(isNoneOption);
          const hasRegular = answer.some(ans => !isNoneOption(ans));

          if (hasNone && hasRegular) {
            console.warn('⚠️ Corrupted data detected - clearing selections');
            answer = [];
          }
        }
      }

      setCurrentAnswer(answer);
    }
  }, [currentQuestionIndex, questions]);

  const fetchCategoryQuestions = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch questions for this category (backend uses category mapping)
      const response = await axios.get(
        `${API_URL}/qualifications/category-questions?category=${category}&t=${Date.now()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        }
      );

      setQuestions(response.data.questions || []);
      setStats({
        answered: response.data.answeredCount || 0,
        total: response.data.totalCount || 0
      });

      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load category questions');
      setLoading(false);
    }
  };

  const handleAnswerChange = (answer) => {
    setCurrentAnswer(answer);
  };

  const handleNext = async () => {
    if (!currentAnswer || (Array.isArray(currentAnswer) && currentAnswer.length === 0)) {
      setError('Please select an answer before proceeding');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const currentQuestion = questions[currentQuestionIndex];

      const response = await axios.post(
        `${API_URL}/qualifications/submit-answer`,
        {
          questionId: currentQuestion._id,
          answer: currentAnswer,
          category: category,
          subcategory: subcategory
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setSuccess('Answer saved successfully!');
      
      // Update stats
      setStats(prev => ({
        ...prev,
        answered: response.data.answeredCount || prev.answered
      }));

      // Move to next question or finish
      if (currentQuestionIndex + 1 < questions.length) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      } else {
        // Completed this category
        setSuccess('✓ You completed all questions in this category!');
        setTimeout(() => {
            navigate('/survey-profiler');
        }, 2000);
      }

      setSaving(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save answer');
      setSaving(false);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const renderAnswerField = (question) => {
    if (question.questionType === 'radio' || question.questionType === 'dropdown') {
      return (
        <div className="options-container">
          {question.options.map((option, index) => (
            <label key={index} className="option-label">
              <input
                type="radio"
                name={`question-${question._id}`}
                value={option.value || option.label}
                checked={currentAnswer === (option.value || option.label)}
                onChange={(e) => handleAnswerChange(e.target.value)}
                disabled={saving}
              />
              <span>{option.label || option.value}</span>
            </label>
          ))}
        </div>
      );
    }

    if (question.questionType === 'checkbox') {
      return (
        <div className="options-container">
          {question.options.map((option, index) => (
            <label key={index} className="option-label">
              <input
                type="checkbox"
                name={`question-${question._id}`}
                value={option.value || option.label}
                checked={currentAnswer.includes(option.value || option.label)}
                onChange={(e) => {
                  const value = e.target.value;
                  if (e.target.checked) {
                    setCurrentAnswer([...currentAnswer, value]);
                  } else {
                    setCurrentAnswer(currentAnswer.filter(ans => ans !== value));
                  }
                }}
                disabled={saving}
              />
              <span>{option.label || option.value}</span>
            </label>
          ))}
        </div>
      );
    }

    if (question.questionType === 'text') {
      return (
        <input
          type="text"
          value={currentAnswer || ''}
          onChange={(e) => handleAnswerChange(e.target.value)}
          placeholder="Enter your answer"
          disabled={saving}
          className="text-answer-input"
        />
      );
    }

    return <p>Unsupported question type</p>;
  };

  if (loading) {
    return (
      <div className="profiler-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading category questions...</p>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="profiler-container">
        <div className="empty-state">
          <h2>No questions available</h2>
          <p>All questions in this category have been answered or are unavailable.</p>
                <Link to="/survey-profiler" className="btn-primary">
                    Back to Categories
          </Link>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
  const earnedPoints = stats.answered * 10;
  const remainingPoints = (questions.length - stats.answered) * 10;

  return (
    <div className="profiler-container">
      <div className="profiler-header">
              <Link to="/survey-profiler" className="back-link">
                  ← Back to Categories
        </Link>
        <div className="header-info">
                  <h2>{categoryInfo?.title}</h2>
        </div>
      </div>

      <div className="profiler-progress-section">
        <div className="progress-info">
          <div className="progress-stat">
            <span className="stat-label">Remaining Points</span>
            <span className="stat-value">{remainingPoints} 🎁</span>
          </div>
          <div className="progress-stat">
            <span className="stat-label">Earned Points</span>
            <span className="stat-value">{earnedPoints} ✨</span>
          </div>
          <div className="progress-stat">
            <span className="stat-label">Progress</span>
            <span className="stat-value">{stats.answered}/{questions.length}</span>
          </div>
        </div>
        <div className="progress-bar-container">
          <div className="progress-bar">
            <div className="progress-bar-fill" style={{ width: `${progress}%` }}></div>
          </div>
          <span className="progress-percentage">{Math.round(progress)}%</span>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="question-card">
        <div className="question-header">
          <h3 className="question-text">{currentQuestion.question}</h3>
          <span className="question-counter">
            Question {currentQuestionIndex + 1} of {questions.length}
          </span>
        </div>

        <div className="answer-section">
          {renderAnswerField(currentQuestion)}
        </div>

        <div className="question-actions">
          {currentQuestionIndex > 0 && (
            <button
              onClick={handlePreviousQuestion}
              disabled={saving}
              className="btn-secondary"
            >
              ← Previous Question
            </button>
          )}
          <button
            onClick={handleNext}
            disabled={saving || !currentAnswer || (Array.isArray(currentAnswer) && currentAnswer.length === 0)}
            className="btn-primary"
          >
            {saving ? 'Saving...' : currentQuestionIndex + 1 === questions.length ? 'Submit & Finish' : 'Next Question'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SurveyProfilerCategoryQuestions;
